import React from 'react';
import styled from 'styled-components';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import theme from '../../../../theme';
import { rgba } from 'polished';
import PropTypes from 'prop-types';

export const customStyles = {
	control: (base, state) => ({
		...base,
		boxShadow: 'none',
		backgroundColor: theme['midnight-base'],
		borderColor: state.isDisabled
			? rgba(theme['silver-darken-15'], 0.5)
			: state.selectProps.isInvalid
				? rgba(theme['red-base'], 0.67)
				: state.isFocused
					? theme['blue-base']
					: theme['silver-darken-15'],
		borderWidth: 3,
		fontWeight: '500',
		transition: 'all 0.2s',
		minHeight: '2.75rem',
		opacity: state.isDisabled ? '0.33' : '1'
	}),
	singleValue: (base) => ({
		...base,
		color: theme['white']
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: theme['blue-darken-15'],
		'&:hover': {
			backgroundColor: theme['blue-darken-20']
		}
	}),
	multiValueLabel: (base) => ({
		...base,
		color: theme['white']
	}),
	multiValueRemove: (base) => ({
		...base,
		'&:hover': {
			color: theme['white'],
			backgroundColor: theme['blue-darken-20']
		}
	}),
	menu: (base) => ({
		...base,
		backgroundColor: theme['midnight-lighten-10']
	}),
	menuList: (base) => ({
		...base,
		color: theme['white'],
		fontSize: '0.9rem',
		fontWeight: '500'
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isSelected
			? theme['blue-base']
			: state.isFocused
				? theme['midnight-lighten-20']
				: 'transparent',
		color: state.isDisabled
			? theme['silver-base']
			: state.isSelected
				? theme['white']
				: 'inherit',
		':active': {
			backgroundColor: !state.isDisabled && (state.isSelected ? theme['blue-darken-5'] : theme['midnight-lighten-15'])
		}
	}),
	indicatorSeparator: (base) => ({
		...base,
		opacity: '0.5'
	}),
	input: (base) => ({
		...base,
		height: 'auto',
		color: theme['white'],
		fontWeight: '500'
	}),
	valueContainer: (base) => ({
		...base,
		lineHeight: '1.5'
	})
};

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<svg
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				stroke={theme['white']}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<polyline points="6 9 12 15 18 9" />
			</svg>
		</components.DropdownIndicator>
	);
};

const StyledDropdownIndicator = styled(DropdownIndicator)`
	padding: 0.5rem !important;
`;

// Override default input height for react-select input
const StyledInput = styled(components.Input)`
	input {
		height: auto;
	}
`;

const Input = (props) => (
	<StyledInput {...props} />
);

// When focus moves from the input into the open menu, react-select puts it
// straight back on the input from its own blur handler with a bare
// `this.inputRef.focus()` — no null check. React clears that ref as the input is
// torn down, so a blur delivered mid-teardown reaches the handler with nothing
// to focus and throws "Cannot read properties of null (reading 'focus')",
// taking the page down with it.
//
// The handler can't be reached through `components`: with `isSearchable={false}`
// react-select renders its internal DummyInput, which is not overridable. It is
// an instance field though, so wrapping it on the instance covers every render
// path. Attach the returned ref to the Select. See PLATFORM-4058.
export const useSafeInputBlur = () => {
	const selectRef = React.useRef(null);

	React.useEffect(() => {
		const select = selectRef.current;
		if(!select || typeof select.onInputBlur !== 'function') {
			return undefined;
		}

		const onInputBlur = select.onInputBlur;
		select.onInputBlur = (event) => {
			// Nothing left to re-focus, so react-select would only throw. Bailing
			// matches what it does anyway once the input is back in hand.
			if(!select.inputRef) {
				return;
			}
			onInputBlur.call(select, event);
		};

		return () => {
			select.onInputBlur = onInputBlur;
		};
	}, []);

	return selectRef;
};

const SelectMenu = ({ ...props }) => (
	<Select
		ref={useSafeInputBlur()}
		id={props['data-testid'] || ''}
		data-testid={props['data-testid'] || ''}
		classNamePrefix="px-react-select"
		styles={customStyles}
		components={{ DropdownIndicator: StyledDropdownIndicator, Input, ...props.components }}
		{...props}
	/>
);

SelectMenu.propTypes = {
	components: PropTypes.object,
	'data-testid': PropTypes.string
};

export const AsyncSelectMenu = ({ styles, ...props }) => (
	<AsyncSelect
		ref={useSafeInputBlur()}
		className={props.className || ''}
		classNamePrefix="px-react-select"
		styles={styles || customStyles}
		components={{ DropdownIndicator: StyledDropdownIndicator, Input, ...props.components }}
		{...props}
	/>
);

AsyncSelectMenu.propTypes = {
	styles: PropTypes.object,
	className: PropTypes.string,
	components: PropTypes.object
};

export default SelectMenu;